import React, { useMemo } from 'react';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../../../hooks/hooks';
import TiminatorTableSettings, { TiminatorTableColumn } from '../../../../../components/shared/table-v2/types';
import { TiminatorType } from '../../../../../features/types/types';
import { Lx } from '../../../../../i18n/consts';
import { StatusTag } from '../../../../../components/shared/status-tag';
import { DeleteIconButton } from '../../../../../components/shared/delete-icon-button';
import { StyledMobileDefaultText } from '../../../../../components/shared/styled-mobile-default-text';
import { normalizeString } from '../../../../../utils/strings';
import { buildSearchValues, filterByStatusAndSearch, getStatusSortValue, getStatusValue } from '../../../../../utils/table-filters';

export const useProjectTypesTable = (
    search: string,
    selectedStatus: number,
    isMobile: boolean
): TiminatorTableSettings<TiminatorType> => {
    const intl = useIntl();
    const projectTypes = useAppSelector(store => store.types.projects.items);
    const defaultLabel = intl.formatMessage({ id: Lx.General.DEFAULT });

    const columns = useMemo((): TiminatorTableColumn<TiminatorType>[] => {
        const baseColumns: TiminatorTableColumn<TiminatorType>[] = [];
        const getTypeSortValue = (rowData: TiminatorType) => (rowData.default ? 1 : 0);

        if (isMobile) {
            baseColumns.push({
                id: 'name',
                label: intl.formatMessage({ id: Lx.General.NAME }),
                sortable: true,
                getSortValue: (rowData: TiminatorType) => normalizeString(rowData.name),
                format: (rowData: TiminatorType) => {
                    return (
                        <span>
                            <div>{rowData.name}</div>
                            {rowData.default && (
                                <StyledMobileDefaultText text={intl.formatMessage({ id: Lx.General.DEFAULT })} />
                            )}
                        </span>
                    );
                },
            });
        } else {
            baseColumns.push(
                {
                    id: 'name',
                    label: intl.formatMessage({ id: Lx.General.NAME }),
                    sortable: true,
                    getSortValue: (rowData: TiminatorType) => normalizeString(rowData.name),
                    format: (rowData: TiminatorType) => rowData.name,
                },
                {
                    id: 'default',
                    label: intl.formatMessage({ id: Lx.General.TYPE }),
                    sortable: true,
                    getSortValue: getTypeSortValue,
                    format: (rowData: TiminatorType) => {
                        return (
                            <span>
                                {rowData.default && (
                                    <div>{intl.formatMessage({ id: Lx.General.DEFAULT })}</div>
                                )}
                            </span>
                        );
                    },
                }
            );
        }

        baseColumns.push(
            {
                id: 'status',
                label: 'Status',
                align: isMobile ? 'right' : 'left',
                sortable: true,
                getSortValue: (rowData: TiminatorType) => getStatusSortValue(rowData.active),
                format: (rowData: TiminatorType) => {
                    return (
                        <StatusTag status={rowData.active ? 'active' : 'suspended'}>
                            <span>
                                {rowData.active ? <FormattedMessage id="active" /> : <FormattedMessage id="suspended" />}
                            </span>
                        </StatusTag>
                    );
                },
                width: isMobile ? '30%' : undefined,
            },
            {
                id: 'deleteButton',
                format: (rowData: TiminatorType) => {
                    return <DeleteIconButton />;
                },
                width: isMobile ? '24px' : '64px',
            }
        )

        return baseColumns;
    }, [isMobile, intl]);

    const data = useMemo(() => (
        filterByStatusAndSearch(
            projectTypes,
            selectedStatus,
            projectType => projectType.active,
            search,
            projectType => buildSearchValues([
                projectType.name,
                projectType.default ? defaultLabel : '',
                getStatusValue(intl, projectType.active),
            ])
        )
    ), [projectTypes, search, selectedStatus, defaultLabel, intl]);

    const isEmpty = projectTypes.length === 0;

    return {
        columns,
        data,
        isEmpty,
    };
};
